import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import {
  FileText,
  CheckSquare,
  Lightbulb,
  Plus,
  ChevronDown,
  Search,
  ArrowUpDown,
  List,
  LayoutGrid,
  Keyboard,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/EmptyState";
import { Thumb } from "@/components/Thumb";
import { useItemsStore } from "@/lib/stores/itemsStore";

const TYPE_META = {
  nota: { label: "Nota", icon: FileText },
  tarea: { label: "Tarea", icon: CheckSquare },
  idea: { label: "Idea", icon: Lightbulb },
};

const TABS = [
  { key: "todas", label: "Todos" },
  { key: "tarea", label: "Tareas" },
  { key: "nota", label: "Notas" },
  { key: "idea", label: "Ideas" },
];

const PROJECT_CHIP = {
  p1: "bg-teal-50 text-teal-700 dark:bg-teal-950/60 dark:text-teal-400",
  p2: "bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-400",
  p3: "bg-violet-50 text-violet-700 dark:bg-violet-950/50 dark:text-violet-400",
  p4: "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300",
  p5: "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300",
};

const groupOf = (a) => {
  if (a.type === "tarea" && !a.done) return "Pendientes";
  const age = Date.now() - new Date(a.createdAt);
  if (age < 86400000) return "Hoy";
  if (age < 7 * 86400000) return "Esta semana";
  return "Anteriores";
};

const GROUPS = [
  { name: "Pendientes", dot: "bg-amber-600" },
  { name: "Hoy", dot: "bg-green-600" },
  { name: "Esta semana", dot: "bg-sky-600" },
  { name: "Anteriores", dot: "bg-stone-400" },
];

export default function Tareas() {
  const { actions, items, projects, toggleAction, select } = useItemsStore();
  const [, navigate] = useLocation();
  const [filter, setFilter] = useState("todas");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    let list = actions;
    if (filter !== "todas") list = list.filter((a) => a.type === filter);
    if (q.trim()) list = list.filter((a) => a.title.toLowerCase().includes(q.trim().toLowerCase()));
    return list;
  }, [actions, filter, q]);

  const grouped = useMemo(() => {
    const g = {};
    for (const a of filtered) (g[groupOf(a)] = g[groupOf(a)] || []).push(a);
    return GROUPS.filter((gr) => g[gr.name]?.length).map((gr) => [gr, g[gr.name]]);
  }, [filtered]);

  const countType = (t) => actions.filter((a) => t === "todas" || a.type === t).length;
  const pendientes = actions.filter((a) => a.type === "tarea" && !a.done).length;
  const hoy = actions.filter((a) => Date.now() - new Date(a.createdAt) < 86400000).length;
  const semana = actions.filter((a) => Date.now() - new Date(a.createdAt) < 7 * 86400000).length;
  const byType = { tarea: countType("tarea"), nota: countType("nota"), idea: countType("idea") };
  const total = actions.length || 1;
  const pct = {
    tarea: Math.round((byType.tarea / total) * 100),
    nota: Math.round((byType.nota / total) * 100),
    idea: Math.round((byType.idea / total) * 100),
  };

  const projectOf = (itemId) => projects.find((p) => p.itemIds.includes(itemId));

  const openSource = (itemId) => {
    select(itemId);
    navigate("/biblioteca");
  };

  const Row = ({ a }) => {
    const Icon = TYPE_META[a.type].icon;
    const source = items.find((i) => i.id === a.itemId);
    const project = a.itemId ? projectOf(a.itemId) : null;
    return (
      <div className="flex items-center gap-3 px-4 py-3" data-testid={`action-row-${a.id}`}>
        {a.type === "tarea" ? (
          <Checkbox
            checked={!!a.done}
            onCheckedChange={() => toggleAction(a.id)}
            aria-label={`Marcar «${a.title}»`}
            data-testid={`action-checkbox-${a.id}`}
          />
        ) : (
          <div className="w-7 h-7 rounded-md bg-secondary grid place-items-center shrink-0">
            <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
          </div>
        )}
        <p className={cn("min-w-0 flex-1 basis-2/5 text-[15px] font-medium truncate", a.done && "line-through text-muted-foreground")}>
          {a.title}
        </p>
        {source ? (
          <button
            type="button"
            onClick={() => openSource(source.id)}
            className="hidden lg:flex items-center gap-2 min-w-0 flex-1 basis-2/5 text-left group"
            data-testid={`action-source-${a.id}`}
          >
            <Thumb platform={source.platform} size="mini" className="w-6 h-6 rounded" />
            <span className="text-[13px] text-muted-foreground truncate group-hover:text-foreground group-hover:underline">
              {source.title}
            </span>
          </button>
        ) : (
          <span className="hidden lg:block flex-1 basis-2/5" />
        )}
        {project ? (
          <span className={cn("hidden md:inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap", PROJECT_CHIP[project.id] || "bg-secondary text-muted-foreground")}>
            {project.name}
          </span>
        ) : (
          <span className="hidden md:inline-block w-20" />
        )}
        <span className="text-xs text-muted-foreground shrink-0 tabular-nums w-14 text-right">
          {new Date(a.createdAt).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
        </span>
      </div>
    );
  };

  return (
    <div className="h-full overflow-y-auto scrollbar-thin" data-testid="tasks-page">
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1400px] mx-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl tracking-tight">Tareas</h1>
            <p className="mt-1.5 text-muted-foreground text-[15px]">Convierte lo importante en trabajo y vuelve cuando toque.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="relative hidden lg:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar tareas…" className="pl-9 w-56" />
            </div>
            <Button onClick={() => toast("Crea acciones desde el detalle de cualquier contenido.")} data-testid="new-action-button">
              <Plus className="h-4 w-4" strokeWidth={2} /> Nueva acción
              <ChevronDown className="h-3.5 w-3.5 opacity-70" strokeWidth={2} />
            </Button>
          </div>
        </div>

        {/* Tabs por tipo */}
        <div className="mt-4 border-b flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setFilter(t.key)}
              data-testid={`task-filter-${t.key}`}
              className={cn(
                "relative px-3 py-2 text-sm font-medium transition-colors",
                filter === t.key ? "text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}{" "}
              <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground">
                {countType(t.key)}
              </span>
              {filter === t.key && <span className="absolute inset-x-2 -bottom-px h-0.5 bg-primary rounded-full" />}
            </button>
          ))}
        </div>

        {/* Fila de filtros */}
        <div className="mt-3 flex items-center gap-2 overflow-x-auto scrollbar-thin pb-1">
          {["Estado: Pendientes", "Proyecto: Todos", "Fecha: Todas", "Etiquetas", "Más filtros"].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => toast("Filtros avanzados en la versión final.")}
              className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-[13px] text-muted-foreground hover:text-foreground whitespace-nowrap shrink-0"
            >
              {f} <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={1.75} />
            </button>
          ))}
          <span className="flex-1" />
          <button
            type="button"
            onClick={() => toast("Orden configurable en la versión final.")}
            className="hidden lg:inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground whitespace-nowrap"
          >
            <ArrowUpDown className="h-3.5 w-3.5" strokeWidth={1.75} /> Ordenar: Fecha de creación
            <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={1.75} />
          </button>
          <div className="hidden lg:flex rounded-md border overflow-hidden shrink-0">
            <span className="p-1.5 bg-accent text-accent-foreground">
              <List className="h-3.5 w-3.5" strokeWidth={1.75} />
            </span>
            <button type="button" aria-label="Vista tablero" onClick={() => toast("Vista tablero en la versión final.")} className="p-1.5 text-muted-foreground hover:bg-secondary border-l">
              <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-6 xl:grid-cols-[1fr,320px] items-start">
          {/* Lista agrupada */}
          <div className="min-w-0">
            {filtered.length === 0 ? (
              <div className="rounded-lg border bg-card">
                <EmptyState
                  icon={CheckSquare}
                  title="Convierte lo que guardas en notas, tareas e ideas."
                  hint="Desde el detalle de cualquier contenido: «Crear nota», «Crear tarea» o «Guardar idea»."
                  testId="tasks-empty"
                />
              </div>
            ) : (
              <div className="space-y-5" data-testid="actions-list">
                {grouped.map(([gr, list]) => (
                  <section key={gr.name}>
                    <p className="flex items-center gap-2 text-sm font-semibold mb-2">
                      <span className={cn("w-2 h-2 rounded-full", gr.dot)} /> {gr.name}{" "}
                      <span className="text-muted-foreground font-normal tabular-nums">({list.length})</span>
                    </p>
                    <div className="rounded-lg border bg-card divide-y overflow-hidden">
                      {list.map((a) => (
                        <Row key={a.id} a={a} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>

          {/* Raíl derecho */}
          <div className="space-y-4 xl:sticky xl:top-6">
            <section className="rounded-lg border bg-card p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold">Resumen</h2>
                <button type="button" onClick={() => toast("Informe en la versión final.")} className="text-[13px] text-primary hover:underline">
                  Ver informe
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[
                  { v: pendientes, l: "Pendientes", cls: "bg-amber-50 dark:bg-amber-950/40" },
                  { v: hoy, l: "Hoy", cls: "bg-green-50 dark:bg-green-950/40" },
                  { v: semana, l: "Esta semana", cls: "bg-sky-50 dark:bg-sky-950/40" },
                  { v: actions.length, l: "Totales", cls: "bg-secondary" },
                ].map((s) => (
                  <div key={s.l} className={cn("rounded-lg px-1 py-2.5", s.cls)}>
                    <p className="text-lg font-semibold tabular-nums">{s.v}</p>
                    <p className="text-[10px] text-muted-foreground leading-tight">{s.l}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border bg-card p-4">
              <h2 className="text-sm font-semibold mb-3">Por tipo</h2>
              <div className="flex items-center gap-4">
                <div
                  className="w-24 h-24 rounded-full grid place-items-center shrink-0"
                  style={{
                    background: `conic-gradient(#0F766E 0 ${pct.tarea}%, #57534E ${pct.tarea}% ${pct.tarea + pct.nota}%, #B45309 ${pct.tarea + pct.nota}% 100%)`,
                  }}
                  role="img"
                  aria-label={`Tareas ${pct.tarea}%, notas ${pct.nota}%, ideas ${pct.idea}%`}
                >
                  <div className="w-14 h-14 rounded-full bg-card" />
                </div>
                <div className="space-y-1.5 text-[13px]">
                  <p className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-teal-700" /> Tareas{" "}
                    <span className="text-muted-foreground tabular-nums">
                      {byType.tarea} ({pct.tarea}%)
                    </span>
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-stone-600" /> Notas{" "}
                    <span className="text-muted-foreground tabular-nums">
                      {byType.nota} ({pct.nota}%)
                    </span>
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-700" /> Ideas{" "}
                    <span className="text-muted-foreground tabular-nums">
                      {byType.idea} ({pct.idea}%)
                    </span>
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-lg border bg-card p-4">
              <h2 className="text-sm font-semibold mb-3">Proyectos con más pendientes</h2>
              <div className="space-y-2">
                {projects
                  .filter((p) => p.taskCount)
                  .sort((a, b) => b.taskCount - a.taskCount)
                  .slice(0, 4)
                  .map((p) => {
                    const Icon = p.icon;
                    return (
                      <button key={p.id} type="button" onClick={() => navigate("/proyectos")} className="w-full flex items-center gap-2.5 text-left hover:bg-secondary/60 rounded-md p-1 -m-1 transition-colors">
                        {Icon && (
                          <span className={cn("w-6 h-6 rounded-md grid place-items-center shrink-0", p.iconCls)}>
                            <Icon className="h-3 w-3" strokeWidth={1.75} />
                          </span>
                        )}
                        <span className="flex-1 text-[13px] truncate">{p.name}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">{p.taskCount}</span>
                      </button>
                    );
                  })}
              </div>
              <button type="button" onClick={() => navigate("/proyectos")} className="mt-2.5 text-[13px] text-primary hover:underline">
                Ver todos los proyectos
              </button>
            </section>

            <section className="rounded-lg border bg-card p-4">
              <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Keyboard className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} /> Atajos
              </h2>
              <div className="space-y-1.5 text-[13px] text-muted-foreground">
                {[
                  ["Nueva tarea", "T"],
                  ["Nueva idea", "I"],
                  ["Atajos de teclado", "⌘ /"],
                ].map(([l, k]) => (
                  <p key={l} className="flex items-center justify-between">
                    {l} <kbd className="rounded border bg-secondary px-1.5 py-0.5 text-[11px]">{k}</kbd>
                  </p>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
